import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useRoute } from '@react-navigation/native';

import { useUserContext } from '../../../common/utils/context';
import { isDocketDoneForMyDay } from '../../../common/utils/myDay/driverDaySteps';
import { getLocalTime } from '../../../common/utils/time/time';
import { getUserName } from '../../../common/utils/user/getUserName';
import { Card, EmptyState, ListRow, Screen, SectionTitle } from '../../design/components';
import RunStopsList from './components/RunStopsList';
import BriefHtml from './startOfDay/BriefHtml';
import { GET_MY_RUN_OVERVIEW } from './myRunsQueries';

const MyRunOverviewView = () => {
	const { user, tenant } = useUserContext();
	const route = useRoute();
	const { data, loading, error, refetch } = useQuery(GET_MY_RUN_OVERVIEW, {
		variables: { filter: { driver: user?.id, serviceRunIds: [route.params?.id] } },
		skip: !user?.id || !route.params?.id,
		fetchPolicy: 'cache-and-network',
	});
	const run = data?.getServiceRuns?.items?.[0];
	const dockets = useMemo(() => [...(run?.serviceDockets || [])].sort((a, b) => a.order - b.order), [run?.serviceDockets]);

	return (
		<Screen
			backLabel="My runs"
			heading={run?.route?.name || 'Run overview'}
			subheading={run?.planned ? `${getLocalTime(tenant?.timezone, run.planned).format('dddd D MMMM YYYY')} · View only` : 'View only'}
			isLoading={loading && !run}
			error={error ? 'This run could not be loaded.' : undefined}
			onRetry={refetch}
		>
			{run ? (
				<>
					<Card>
						<ListRow title="Driver" subtitle={getUserName(run.driver)} tone="card" />
						<ListRow title="Vehicle" subtitle={run.vehicle?.name || 'No vehicle'} tone="card" />
						{Boolean(run.warehouse?.name) && <ListRow title="Warehouse" subtitle={run.warehouse.name} tone="card" />}
						{Boolean(run.returnWarehouse?.name) && <ListRow title="Return warehouse" subtitle={run.returnWarehouse.name} tone="card" />}
						{Boolean(run.startLocation?.name) && <ListRow title="Start" subtitle={run.startLocation.name} tone="card" />}
						{Boolean(run.endLocation?.name) && <ListRow title="End" subtitle={run.endLocation.name} tone="card" />}
					</Card>
					{Boolean(run.brief) && <Card><BriefHtml html={run.brief} /></Card>}
					<SectionTitle title="Stops in route order" meta={`${dockets.length} ${dockets.length === 1 ? 'stop' : 'stops'}`} />
					<RunStopsList dockets={dockets} isDocketDone={isDocketDoneForMyDay} collapseDoneSites={false} showStopNumbers={true} showCompletedDetails={true} />
					{!dockets.length && <EmptyState title="No stops" message="This run has no stops yet." />}
				</>
			) : <EmptyState title="Run unavailable" message="This run is no longer assigned to you." />}
		</Screen>
	);
};

export default MyRunOverviewView;
